import { OnChanges, Component, Input, SimpleChanges, ViewContainerRef, ViewChild, ComponentRef, AfterViewInit, OnDestroy } from '@angular/core';
import { ComponentLocator } from '../component-locator';
import { LoaderComponent } from '../shared/loader/loader.component';
import { AsyncPipe } from '@angular/common';
import { WingViewModelTypeInspector } from '../../view-models/decorators/wing-view-model-type.decorator';
import { isConfigurableWingViewModel } from '../../view-models/wing/is-configurable-wing-view-model';
import type { ConfigurableWingViewModelInterface, WingViewComponentInterface, WingViewModelInterface } from '@nts/std/interfaces';

/**
 * Container component that renders wing panel and its content.
 * Handles:
 * - Dynamic component loading for wing content
 * - Automatic cleanup of dynamically loaded components
 *
 * This is a framework component - typically not used directly by applications.
 * It's used internally by model-container to render the wing panel.
 *
 * @example
 * ```html
 * <nts-wing-container [wingViewModel]="orchestrator.wingVm"></nts-wing-container>
 * ```
 */
@Component({
  selector: 'nts-wing-container',
  templateUrl: './wing-container.component.html',
  styleUrls: ['./wing-container.component.scss'],
  standalone: true,
  imports: [
    LoaderComponent,
    AsyncPipe,
  ]
})
export class WingContainerComponent implements OnChanges, AfterViewInit, OnDestroy {

  /**
   * Wing view model that provides state, configuration, and control.
   * Required input - component will not render without it.
   */
  @Input({ required: true }) wingViewModel!: WingViewModelInterface;

  @ViewChild('wingPlaceHolder', { read: ViewContainerRef, static: true })

  private wingPlaceHolder!: ViewContainerRef;

  private componentRef: ComponentRef<unknown> | null = null;
  private viewInitialized = false;

  constructor(
    protected componentLocator: ComponentLocator) {
  }

  async ngOnChanges(changes: SimpleChanges): Promise<void> {
    if (changes['wingViewModel'] && this.wingViewModel && this.viewInitialized) {
      await this.renderWingComponent();
    }
  }

  async ngAfterViewInit(): Promise<void> {
    this.viewInitialized = true;

    if (this.wingViewModel) {
      await this.renderWingComponent();
    }
  }

  ngOnDestroy(): void {
    this.clearDynamicComponent();
  }

  get configurableWingViewModel(): ConfigurableWingViewModelInterface | null {
    return isConfigurableWingViewModel(this.wingViewModel)
      ? this.wingViewModel
      : null;
  }

  /**
   * Check if toggle button should be shown.
   */
  get showToggle(): boolean {
    const showToggle = this.configurableWingViewModel?.showToggle() ?? true;
    return showToggle === 'alternate'
      ? this.wingViewModel?.panelState === 'open'
      : showToggle;
  }

  get toggleIconKey(): string {
    return this.wingViewModel?.panelState === 'closed' ? 'arrow-back' : 'arrow-forward';
  }

  /**
   * Handle toggle button click.
   * Uses new requestToggle() API if available, falls back to legacy isCollapsed for backward compatibility.
   */
  async onToggleClick(): Promise<void> {
    if (this.configurableWingViewModel) {
      await this.configurableWingViewModel.requestToggle('user');
      return;
    }

    this.wingViewModel.orchestratorViewModel.eventDispatcher.onWingCollapsed
      .next(this.wingViewModel.panelState === 'open');
  }

  /**
   * Dynamically load and render the wing content component.
   * Gets component type from wing view model type, creates instance, initializes it.
   * @private
   */
  private async renderWingComponent(): Promise<void> {
    if (!this.wingPlaceHolder) {
      return;
    }

    this.clearDynamicComponent();
    this.wingPlaceHolder.clear();

    const viewModelType = WingViewModelTypeInspector.getValue(this.wingViewModel);
    const componentType = this.componentLocator.getComponentType(viewModelType);

    this.componentRef = this.wingPlaceHolder.createComponent(componentType);

    const instance = this.componentRef.instance as WingViewComponentInterface;
    instance.wingViewModel = this.wingViewModel;

    return new Promise<void>((resolve) => {
      // Necessary to allow Angular to finish change detection before calling initialize() and postInit().
      setTimeout(
        // eslint-disable-next-line @typescript-eslint/no-misused-promises
        async () => {
          await instance.initialize();
          await this.wingViewModel.postInit();
          resolve();
        },
        0
      );
    });
  }

  /**
   * Clean up dynamically created wing content component.
   * @private
   */
  private clearDynamicComponent(): void {
    if (this.componentRef) {
      this.componentRef.destroy();
      this.componentRef = null;
    }
  }

}
